import { Divider, Drawer, Hidden, IconButton, List, useMediaQuery } from '@material-ui/core';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { ChevronLeft, ExitToApp, FileCopy, Group, GroupWork, RingVolume, Feedback } from '@material-ui/icons';
import clsx from 'clsx';
import { AccessPointNetwork, HeartPulse, VectorPolyline } from 'mdi-material-ui';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import UserInfo from '../../components/user-info';
import { COMPANIES } from '../../constants';
import * as Feature from '../../features';
import PageLink from '../../page-link';

export const properties = {
  width: 240,
};

const DrawerClosedBreakpoint = 2220;
const StickyHeaderBreakpoint = 'md';

const useStyles = makeStyles((theme: Theme) => ({
  drawerPaper: {
    height: '100%',
    position: 'relative',
    overflow: 'visible',
    width: properties.width,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaperClose: {
    [theme.breakpoints.up(DrawerClosedBreakpoint)]: {
      width: 72,
    },
    [theme.breakpoints.down(DrawerClosedBreakpoint)]: {
      position: 'absolute',
      width: 0,
    },
  },
  drawerPaperOpen: {
    [theme.breakpoints.down(StickyHeaderBreakpoint)]: {
      position: 'absolute',
    },
  },
  minimizePaper: {
    position: 'absolute',
    width: 0,
  },
  drawerInner: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 8px',
    ...theme.mixins.toolbar,
  },
  flipIconHorizontal: {
    transform: 'scaleX(-1)',
  },
  fullWidth: {
    overflowX: 'hidden',
    width: '100%',
  },
  bottomSticky: {
    position: 'sticky',
    bottom: 0,
    alignSelf: 'flex-end',
  },
  topSticky: {
    position: 'sticky',
    top: 0,
  },
}));

interface NavigationLinkProps {
  href: string;
  icon: React.ReactElement<any>;
  title: string;
  tooltipTitle?: string;
}

interface Properties {
  isNavigationOpen: boolean;
  removeNavigation: boolean;
  features: Feature.FeatureMap;
  companyId?: string;
  closeNavigation: () => void;
  navigationLinks?: NavigationLinkProps[];
}

const defaultFeatureMap = {
  group: {},
  line: {},
  peripheral: {},
  userPool: {},
};

const Navigation: FC<Properties> = (props) => {
  const {
    isNavigationOpen,
    removeNavigation,
    features = defaultFeatureMap,
    companyId,
    closeNavigation,
    navigationLinks,
  } = props;

  const [t] = useTranslation();
  const theme = useTheme();
  const classes = useStyles();
  const matches = useMediaQuery(theme.breakpoints.down(DrawerClosedBreakpoint));

  const onTransitionEnd = () => {
    return typeof Event !== 'function' ? (Function.prototype as any) : () => window.dispatchEvent(new Event('resize'));
  };

  const closeWhileSmall = matches ? closeNavigation : undefined;

  let administration: React.ReactNode = '';
  let scheduledReports: React.ReactNode = '';

  if (Feature.inAnyUserPool(features)(['IAM.Role', 'IAM.Group', 'IAM.User'], 'M')) {
    administration = (
      <PageLink
        href="/administration"
        icon={<Group />}
        onClick={closeWhileSmall}
        title={t(['shared:administration'], { defaultValue: 'Administration' })}
      />
    );
  }

  const labelLines = t(['shared:lines'], { defaultValue: 'Lines' });
  const labelSensors = t(['shared:sensors'], { defaultValue: 'Sensors' });
  const labelConsolidatedLines = t(['shared:consolidatedLines'], { defaultValue: 'Consolidated lines' });
  const labelMaintenance = t(['shared:maintenance'], { defaultValue: 'Andon' });
  const labelScheduledReports = t(['shared:scheduledReports'], { defaultValue: 'Scheduled reports' });
  const labelCockpit = t(['shared:cockpit'], { defaultValue: 'Cockpit' });
  const labelLogout = t(['shared:logout'], { defaultValue: 'Logout' });
  const labelFeedback = t(['shared:feedback'], { defaultValue: 'Feedback' });

  let cockpit: React.ReactNode = '';
  const cockpitAvailable = [COMPANIES.BLACKBIRD, COMPANIES.BLACKBIRD_STAGING, COMPANIES.DITSCH].some(
    (cockpitPool) => cockpitPool === companyId,
  );
  if (cockpitAvailable) {
    cockpit = (
      <PageLink
        href="/cockpit?id=root"
        icon={<HeartPulse />}
        title={labelCockpit}
        tooltipTitle={isNavigationOpen ? '' : labelCockpit}
      />
    );
  }

  // FIXME: We are checking for write access to decide if reports are available, but should instead check for read access
  const scheduledReportsAvailable = Feature.inAnyGroup(features)(['Reports.ScheduledReport'], 'M');
  if (scheduledReportsAvailable) {
    scheduledReports = (
      <PageLink
        href="/reports"
        icon={<FileCopy />}
        title={labelScheduledReports}
        tooltipTitle={isNavigationOpen ? '' : labelScheduledReports}
      />
    );
  }

  if (navigationLinks !== undefined && Array.isArray(navigationLinks)) {
    const navLinkRendered = navigationLinks.map((row: NavigationLinkProps, index) => {
      return (
        <PageLink
          key={index}
          href={row.href}
          icon={row.icon}
          title={row.title}
          tooltipTitle={isNavigationOpen ? '' : row.title}
        />
      );
    });

    return (
      <Drawer
        variant="permanent"
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !isNavigationOpen && removeNavigation && classes.minimizePaper,
            !isNavigationOpen && classes.drawerPaperClose,
            isNavigationOpen && classes.drawerPaperOpen,
          ),
        }}
        open={isNavigationOpen}
        onTransitionEnd={onTransitionEnd}
      >
        <div className={classes.drawerInner}>
          <div className={`${classes.topSticky} ${classes.fullWidth}`}>
            <div className={classes.drawerHeader}>
              <Hidden mdUp>
                <UserInfo backgroundTone={'light'} />
              </Hidden>
              <IconButton onClick={closeNavigation}>
                <ChevronLeft />
              </IconButton>
            </div>
            <Divider />
            <List onClick={closeWhileSmall}>{navLinkRendered}</List>
          </div>
          <div className={clsx(classes.bottomSticky, classes.fullWidth)}>
            <List onClick={closeWhileSmall}>
              <PageLink
                href="/auth/logout"
                prefetch={false}
                icon={<ExitToApp className={classes.flipIconHorizontal} />}
                title={labelLogout}
                tooltipTitle={isNavigationOpen ? '' : labelLogout}
              />
            </List>
          </div>
        </div>
      </Drawer>
    );
  } else {
    return (
      <Drawer
        variant="permanent"
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !isNavigationOpen && removeNavigation && classes.minimizePaper,
            !isNavigationOpen && classes.drawerPaperClose,
            isNavigationOpen && classes.drawerPaperOpen,
          ),
        }}
        open={isNavigationOpen}
        onTransitionEnd={onTransitionEnd}
      >
        <div className={classes.drawerInner}>
          <div className={clsx(classes.topSticky, classes.fullWidth)}>
            <div className={classes.drawerHeader}>
              <Hidden mdUp>
                <UserInfo backgroundTone={'light'} />
              </Hidden>
              <IconButton onClick={closeNavigation}>
                <ChevronLeft />
              </IconButton>
            </div>
            <Divider />
            <List onClick={closeWhileSmall}>
              <PageLink
                href="/"
                icon={<VectorPolyline />}
                title={labelLines}
                tooltipTitle={isNavigationOpen ? '' : labelLines}
              />

              <PageLink
                href="/sensors"
                icon={<AccessPointNetwork />}
                title={labelSensors}
                tooltipTitle={isNavigationOpen ? '' : labelSensors}
              />
              <PageLink
                href="/consolidated-lines"
                icon={<GroupWork />}
                title={labelConsolidatedLines}
                tooltipTitle={isNavigationOpen ? '' : labelConsolidatedLines}
              />
              <PageLink
                href="/andon"
                icon={<RingVolume />}
                title={labelMaintenance}
                tooltipTitle={isNavigationOpen ? '' : labelMaintenance}
              />
              {scheduledReports}
              {cockpit}
            </List>
          </div>
          <div className={clsx(classes.bottomSticky, classes.fullWidth)}>
            <List onClick={closeWhileSmall}>
              {administration}
              {/* NOTE: Account Settings is disabled until it is more ready than now. */}
              {/* <Link href="/settings">
                  <ListItem button>
                  <ListItemIcon>
                  <SupervisorAccount />
                  </ListItemIcon>
                  <ListItemText primary="Settings" />
                  </ListItem>
                  </Link> */}
              <PageLink
                href="/feedback"
                icon={<Feedback />}
                title={labelFeedback}
                tooltipTitle={isNavigationOpen ? '' : labelFeedback}
              />
              <PageLink
                href="/auth/logout"
                prefetch={false}
                icon={<ExitToApp className={classes.flipIconHorizontal} />}
                title={labelLogout}
                tooltipTitle={isNavigationOpen ? '' : labelLogout}
              />
            </List>
          </div>
        </div>
      </Drawer>
    );
  }
};

export default Navigation;
